import {
  $applyNodeReplacement,
  TextNode,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

import { getHandleFromCommandEntityText } from './prompt-editor-utils';

export type SerializedSkillCommandNode = Spread<
  {
    handle: string;
    skillId?: string;
  },
  SerializedTextNode
>;

export class SkillCommandNode extends TextNode {
  __handle: string;
  __skillId?: string;

  constructor(text = '', handle = getHandleFromCommandEntityText(text), skillId?: string, key?: NodeKey) {
    super(text, key);
    this.__handle = handle;
    this.__skillId = skillId;
  }

  static getType(): string {
    return 'skill-command';
  }

  static clone(node: SkillCommandNode): SkillCommandNode {
    return new SkillCommandNode(node.__text, node.__handle, node.__skillId, node.__key);
  }

  static importJSON(serializedNode: SerializedSkillCommandNode): SkillCommandNode {
    return new SkillCommandNode(serializedNode.text, serializedNode.handle, serializedNode.skillId).updateFromJSON(
      serializedNode,
    );
  }

  updateFromJSON(serializedNode: SerializedSkillCommandNode): this {
    const self = super.updateFromJSON(serializedNode) as this;
    self.__handle = serializedNode.handle ?? getHandleFromCommandEntityText(serializedNode.text);
    self.__skillId = serializedNode.skillId;
    return self;
  }

  exportJSON(): SerializedSkillCommandNode {
    return {
      ...super.exportJSON(),
      handle: this.__handle,
      ...(this.__skillId ? { skillId: this.__skillId } : {}),
      type: 'skill-command',
      version: 1,
    };
  }

  getHandle(): string {
    return this.getLatest().__handle;
  }

  getSkillId(): string | undefined {
    return this.getLatest().__skillId;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.classList.add('text-brand-300');
    element.dataset.skillCommand = this.__handle;
    if (this.__skillId) {
      element.dataset.skillId = this.__skillId;
    }
    return element;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const needsReplacement = super.updateDOM(prevNode, dom, config);

    if (prevNode.__handle !== this.__handle) {
      dom.dataset.skillCommand = this.__handle;
    }

    if (prevNode.__skillId !== this.__skillId) {
      if (this.__skillId) {
        dom.dataset.skillId = this.__skillId;
      } else {
        delete dom.dataset.skillId;
      }
    }

    return needsReplacement;
  }

  isTextEntity(): true {
    return true;
  }
}

export function $createSkillCommandNode(text: string, skillId?: string): SkillCommandNode {
  return $applyNodeReplacement(new SkillCommandNode(text, getHandleFromCommandEntityText(text), skillId));
}

export function $isSkillCommandNode(node: LexicalNode | null | undefined): node is SkillCommandNode {
  return node instanceof SkillCommandNode;
}
